import { createContext, useCallback, useContext, useMemo } from 'react';
import useLocalStorage from './useLocalStorage';
import { currencies } from '../utils/currencies';

const CurrencyContext = createContext(null);

export function CurrencyProvider({ children }) {
  const [currencyCode, setCurrencyCode] = useLocalStorage('zlux-currency', 'VND');

  const currency = useMemo(
    () => currencies.find((item) => item.code === currencyCode) || currencies.find((item) => item.code === 'VND'),
    [currencyCode],
  );

  const formatCurrency = useCallback((valueInVnd) => {
    const amount = (Number(valueInVnd) || 0) / currency.vndPerUnit;
    return new Intl.NumberFormat(currency.locale || 'en-US', {
      style: 'currency',
      currency: currency.code,
    }).format(amount);
  }, [currency]);

  // Convert an amount typed in the selected display currency into the app's VND storage unit.
  const toVnd = useCallback((amountInSelectedCurrency) => (
    (Number(amountInSelectedCurrency) || 0) * currency.vndPerUnit
  ), [currency]);

  // Convert a VND amount from storage into the selected display currency, rounded for form inputs.
  const fromVnd = useCallback((valueInVnd) => (
    Math.round(((Number(valueInVnd) || 0) / currency.vndPerUnit) * 100) / 100
  ), [currency]);

  const value = useMemo(() => ({
    currencyCode, setCurrencyCode, currency, formatCurrency, toVnd, fromVnd,
  }), [currencyCode, setCurrencyCode, currency, formatCurrency, toVnd, fromVnd]);

  return <CurrencyContext.Provider value={value}>{children}</CurrencyContext.Provider>;
}

export function useCurrency() {
  return useContext(CurrencyContext);
}
